import type {
  Editor,
  InputDescription,
  IntegrationDescription,
  Member,
} from "@smartsuite/automation-engine-api";
import {
  SplitType,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import {
  AvatarMenuOption,
  DropdownPanel,
  MenuGroupContent,
  MenuLineSeparator,
  MenuOption,
  MenuOptionWrapper,
  Paragraph,
  PickerInputField,
  Text,
} from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import type { MutableRefObject, Ref } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as R from "remeda";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import {
  ActionLabel,
  MENU_MAX_HEIGHT,
  MENU_MAX_HEIGHT_MARGIN,
} from "../../../../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import { actionsToOptions } from "../../../../utils/action-options";
import { POSITION_AT, POSITION_MY } from "../../../../style";
import { nameToInitials } from "../../../../utils";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import {
  checkIfIsPickerInputField,
  ifLoopingSourceIsActionFind,
} from "../utils";
import { Root, StyledNoFields, StyledReactInputFieldContainer } from "./style";
import {
  useConverters,
  ejectPillFromInput,
  mergeNextPillToInput,
  triggerToOptions,
} from "./utils";
import type {
  ActionOptionGroup,
  PhonePill,
  TriggerActionOption,
} from "./utils";
import { SolutionColorVariables } from "@smartsuite/types";
import { CurrentOption } from "../../containers/CurrentOption";
import { phoneContextSpecificFields } from "../../../../EmailEditor/string-output-types";
import { useLoopingMenu } from "../../../../hooks/use-looping-menu";
import { useActionMemberDirectory } from "../../../../../../../../hooks/use-member-directory";
import { handleTriggerOutput } from "../../../../EmailEditor/utils";
import {
  actionSourceBoundary,
  canOfferActionSources,
  resolveEditorLoopScope,
} from "../../../../utils/action-sources";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";

export const PhoneEditor = observer<{
  allowManual?: boolean;
  description?: InputDescription;
  editor: Editor;
  config: InputCandidate;
  integrations: IntegrationDescription[];
  trigger: TriggerCandidate;
  onChange: (input: InputCandidate) => void;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  /** Set by ArgumentEditor when `actions` is an explicit, already-scoped list
   * (Conditional Logic branch conditions): offer it whole even though there is
   * no owning `action` to bound it (BUG-21770). */
  actionsArePreScoped?: boolean;
  /** The loop whose Current Item this editor may offer, used verbatim when
   * `actionsArePreScoped` is set. See useLoopingMenu (BUG-21770). */
  loopScope?: LoopScope;
  isMultiple: boolean;
  isError?: boolean;
  onClickTrash?: () => void;
  disableSplit?: boolean;
}>(
  ({
    allowManual = true,
    integrations,
    description,
    editor,
    config,
    trigger,
    onChange,
    actions,
    action,
    actionsArePreScoped,
    loopScope,
    isMultiple,
    isError,
    onClickTrash,
    disableSplit,
  }) => {
    const store = useRootStore();
    const { t } = useTranslation();

    const directory = useActionMemberDirectory(action);
    const members = useMemo(() => {
      return directory.members.filter(
        (member: Member) => member.phone.length > 0,
      );
    }, [directory.members]);
    const teams = directory.teams;

    const [isOpen, setIsOpen] = useState(false);
    const [tab, setTab] = useState<
      "root" | "fields" | "members" | "actions" | "teams" | "current"
    >("fields");
    const [triggerFields, setTriggerFields] = useState<TriggerActionOption[]>(
      [],
    );
    const [actionFields, setActionsFields] = useState<ActionOptionGroup[]>([]);
    const [selectedAction, setSelectedAction] = useState<
      ActionOptionGroup | undefined
    >();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [validationError, setValidationError] = useState<string>("");
    const [focus, setFocus] = useState<boolean>(false);
    const buttonRef = useRef<HTMLButtonElement>();
    const [menuHeight, setMenuHeight] = useState<number>(MENU_MAX_HEIGHT);
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const editorWrapperId = `${config.inputId}-editor-wrapper`;
    const [actionType, setActionType] = useState<
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined
    >(config.operatorConfig?.operator?.oneofKind);
    const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
    const rootLooping = store.automations.candidate.looping;
    const looping = resolveEditorLoopScope(
      action,
      rootLooping,
      loopScope,
      actionsArePreScoped,
    );

    const { loopingMenu, loopingFields, loopingAction } = useLoopingMenu({
      loopScope,
      actionsArePreScoped,
      trigger,
      actions,
      integrations,
      types: [ValueDescription_T.PHONE],
      ssFieldTypes: [
        "phonefield",
        "userfield",
        "firstcreatedfield",
        "lastupdatedfield",
      ],
      systemAllowedOptions: [],
      action,
    });

    const { convertInputToPills } = useConverters();

    useEffect(() => {
      if (window.innerHeight < menuHeight * 2 + MENU_MAX_HEIGHT_MARGIN) {
        setMenuHeight(window.innerHeight / 2 - MENU_MAX_HEIGHT_MARGIN);
      } else {
        setMenuHeight(MENU_MAX_HEIGHT);
      }
      window.addEventListener("resize", handleResize, false);

      return () => {
        window.removeEventListener("resize", handleResize, false);
      };
    }, []);

    const handleResize = (): void => {
      if (window.innerHeight < menuHeight * 2 + MENU_MAX_HEIGHT_MARGIN) {
        setMenuHeight(window.innerHeight / 2 - MENU_MAX_HEIGHT_MARGIN);
      } else {
        setMenuHeight(MENU_MAX_HEIGHT);
      }
    };

    useEffect(() => {
      if (actionType === undefined) return;
      if (actionType === "clear") {
        setIsOpen(false);
        setIsCleanAction(true);
      } else {
        setIsCleanAction(false);
      }
      onChange({
        ...config,
        value:
          actionType === "clear"
            ? {
                values: [],
              }
            : config.value,
        operatorConfig: {
          // @ts-expect-error
          operator: {
            oneofKind: actionType,
            [actionType]: {},
          },
        },
      });
    }, [actionType, isOpen]);

    useEffect(() => {
      if (config.validationError !== "") {
        setValidationError(config.validationError ?? "");
      } else {
        setValidationError("");
      }
    }, [config]);

    useEffect(() => {
      setTriggerFields(
        triggerToOptions(
          trigger,
          [ValueDescription_T.STRING],
          [ValueDescription_T.PHONE],
          ["phonefield", "userfield", "firstcreatedfield", "lastupdatedfield"],
          phoneContextSpecificFields,
        ),
      );
    }, [trigger]);

    useEffect(() => {
      if (triggerFields.length) {
        setTab("fields");
      } else if (members.length) {
        setTab("members");
      } else {
        setTab("root");
      }
    }, [triggerFields, members]);

    useEffect(() => {
      if (canOfferActionSources(action, actionsArePreScoped)) {
        setActionsFields(
          actionsToOptions(
            actions,
            actionSourceBoundary(action, actionsArePreScoped),
            integrations,

            [ValueDescription_T.PHONE],
            [
              "phonefield",
              "userfield",
              "firstcreatedfield",
              "lastupdatedfield",
            ],
            [],
            looping,
          ),
        );
      }
    }, [actions, action, integrations, looping, actionsArePreScoped]);

    useEffect(() => {
      if (store.inputToFocus?.inputId === config.inputId) {
        setIsOpen(true);
        const focusTimeoutId = setTimeout(() => {
          const wrapper = document.getElementById(editorWrapperId);
          const editor = wrapper?.querySelector(".picker-input-field__input");
          // @ts-expect-error
          editor?.focus();
          store.inputToFocus = undefined;
        });
        return () => clearTimeout(focusTimeoutId);
      }
    }, []);

    const pillList = useMemo(
      () =>
        convertInputToPills(
          config,
          members,
          teams,
          triggerFields,
          actionFields,
          trigger,
          actions,
          editor,
          loopingFields,
        ),
      [config, members, teams, triggerFields, actionFields, editor],
    );

    const isFieldError = (pills: PhonePill[]): boolean =>
      Boolean(pills.find((item) => item.isError));

    const addCustomPhone = (phone: string): void => {
      onChange(
        mergeNextPillToInput(
          config,
          {
            type: "custom",
            phone,
          },
          isMultiple,
          editor,
        ),
      );
    };

    const isNextPillAllowed = (
      config: InputCandidate,
      isMultiple: boolean,
    ): boolean => {
      if (!isMultiple && config.value?.values.length === 1) {
        if (focus) {
          setFocus(false);
        }
        return false;
      } else {
        return true;
      }
    };

    const triggerOutputs = triggerFields
      .filter(
        (option) =>
          !pillList.some(
            (pill) =>
              pill.type === "triggerOutput" && pill.value === option.outputId,
          ),
      )
      .filter((option) =>
        option.title.toLowerCase().includes(searchQuery.toLowerCase()),
      );

    const unselectedLoopFields =
      loopingFields?.options.filter(
        (option) =>
          !option.contextSpecific &&
          !pillList.some((pill) => pill.value === option.outputId),
      ) ?? [];

    const unselectedLoopFieldsFiltered = unselectedLoopFields.filter(
      (option) =>
        searchQuery.length > 0
          ? option.title.toLowerCase().includes(searchQuery.toLowerCase())
          : true,
    );

    const changeActionType = (
      type:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): void => {
      setActionType(type);
    };

    const handlePickerInputFieldClick = (event: React.MouseEvent): void => {
      setIsOpen(true);
    };

    const handleClickOutside = (event: MouseEvent): void => {
      event.stopPropagation();
      if (!checkIfIsPickerInputField(event.target, config.inputId)) {
        setIsOpen(false);
      }
    };

    return (
      <Root>
        <StyledReactInputFieldContainer id={editorWrapperId}>
          {description !== undefined && (
            <CaptionBlock
              description={description}
              onClickTrash={onClickTrash}
              splitType={config.splitType ?? SplitType.NONE}
              onSplitChange={
                isMultiple && !disableSplit
                  ? (splitType) => {
                      onChange({
                        ...config,
                        splitType,
                      });
                    }
                  : undefined
              }
            />
          )}
          <PickerInputField
            showAddButton={!isCleanAction}
            className={
              isError
                ? `is-error picker-input-field-${config.inputId} ${
                    isCleanAction ? "is-clear" : ""
                  }`
                : `picker-input-field-${config.inputId} ${
                    isCleanAction ? "is-clear" : ""
                  }`
            }
            pattern={/^[+]?[0-9]*([,;+][0-9]*)*$/}
            state={
              isFieldError(pillList) ||
              validationError !== "" ||
              !isInputValidByFieldMissing(config, action)
                ? "error"
                : isOpen || focus
                  ? "active"
                  : "default"
            }
            onChangeFocused={(isFocused, event) => {
              setFocus(isFocused);
              if (
                description?.editor?.hidePopupOnHover !== true &&
                isFocused &&
                event?.target.className !== "field-type-pill__remove"
              ) {
                setIsOpen(true);
              }
            }}
            onInputChange={() => {
              setIsOpen(false);
            }}
            // @ts-expect-error
            buttonRef={buttonRef as Ref<HTMLButtonElement>}
            isOpen={isOpen}
            pills={pillList}
            pillStyle={{
              textSize: "m",
            }}
            onButtonClick={handlePickerInputFieldClick}
            onPillRemove={(index, b, event) => {
              event.stopPropagation();
              onChange(ejectPillFromInput(config, index, editor));
            }}
            allowManualInput={
              !isCleanAction &&
              allowManual &&
              isNextPillAllowed(config, isMultiple)
            }
            onInputAdd={addCustomPhone}
            placeholder={
              isCleanAction
                ? t("automation-form.clean-operator-editor-placeholder")
                : isOpen || focus
                  ? pillList.length > 0
                    ? ""
                    : allowManual
                      ? t("editor.type-or-use-field")
                      : t("editor.use-field")
                  : ""
            }
          >
            {action?.actionReference?.actionId === ActionLabel.updateRecord &&
              description?.operators?.operators !== undefined &&
              description?.operators?.operators.length > 0 && (
                <UpdateValueDropdown
                  operatorConfig={config.operatorConfig}
                  operatorDescriptions={description?.operators.operators}
                  onOpenedChange={setFocus}
                  onChange={changeActionType}
                />
              )}

            {isOpen && (
              <DropdownPanel
                targetElementRef={buttonRef as MutableRefObject<HTMLElement>}
                clickOutsideExcept={[".dropdown-panel"]}
                isActive={isOpen}
                at={POSITION_AT}
                my={POSITION_MY}
                minWidth={300}
                arrowBackground="custom"
                arrowCustomBackground={SolutionColorVariables.sc1}
                panelLineBackground={SolutionColorVariables.sc1}
                portalStyles={getLocalCssColorVariables(store.theme)}
                onClickOutside={handleClickOutside}
                onCloseAnimationEnd={() => {
                  setSearchQuery("");
                }}
              >
                {tab === "root" && (
                  <MenuOptionWrapper>
                    {triggerOutputs.length > 0 && (
                      <MenuOption
                        submenuArrow="right"
                        onClick={() => setTab("fields")}
                      >
                        {t("automation-form.fields-from-trigger")}
                      </MenuOption>
                    )}

                    {actionFields.map(
                      (actionMenu) =>
                        actionMenu?.options.length > 0 && (
                          <MenuOption
                            key={`${actionMenu.name}-${
                              actionMenu.action.actionReference?.instanceId ??
                              ""
                            }`}
                            submenuArrow="right"
                            onClick={() => {
                              setSelectedAction(actionMenu);
                              setTab("actions");
                            }}
                          >
                            {actionMenu.hideNamePrefix === true
                              ? actionMenu.name
                              : t("field-popups.action-popup", {
                                  name: actionMenu.name,
                                })}
                          </MenuOption>
                        ),
                    )}
                    <MenuOption
                      submenuArrow="right"
                      onClick={() => setTab("members")}
                    >
                      {t("editor.members")}
                    </MenuOption>
                    {teams.length > 0 && (
                      <MenuOption
                        submenuArrow="right"
                        onClick={() => setTab("teams")}
                      >
                        {t("editor.teams")}
                      </MenuOption>
                    )}
                    <CurrentOption
                      loopingMenu={loopingMenu}
                      loopScope={looping}
                      actionsArePreScoped={actionsArePreScoped}
                      addPill={(looping) => {
                        // Case when source for looping is the find action
                        if (
                          ifLoopingSourceIsActionFind(looping) ||
                          loopingMenu?.options?.length
                        ) {
                          setTab("current");
                          return;
                        }

                        if (looping.source) {
                          onChange(
                            mergeNextPillToInput(
                              config,
                              {
                                type: "loop",
                                looping,
                                outputId: "",
                              },
                              isMultiple,
                              editor,
                            ),
                          );
                        }
                      }}
                    />
                  </MenuOptionWrapper>
                )}
                {tab === "current" &&
                  unselectedLoopFieldsFiltered !== undefined && (
                    <MenuGroupContent
                      title={t("general.current-item")}
                      onSearchChange={(query) => setSearchQuery(query)}
                      search={unselectedLoopFieldsFiltered.length > 5}
                      onNavigateBack={() => {
                        setTab("root");
                        setSearchQuery("");
                      }}
                    >
                      {unselectedLoopFieldsFiltered.map((option) => (
                        <MenuOption
                          key={option.outputId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            if (loopingAction?.actionReference) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  {
                                    type: "actionOutput",
                                    actionReference:
                                      loopingAction?.actionReference,
                                    outputId: option.outputId,
                                  },
                                  isMultiple,
                                  editor,
                                ),
                              );
                              return;
                            }

                            const triggerConfig = handleTriggerOutput(
                              trigger,
                              option,
                            );
                            if (triggerConfig) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  triggerConfig,
                                  isMultiple,
                                  editor,
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {option.title}
                          </Text>
                        </MenuOption>
                      ))}
                      {unselectedLoopFieldsFiltered.length === 0 && (
                        <StyledNoFields>No results</StyledNoFields>
                      )}
                    </MenuGroupContent>
                  )}

                {tab === "fields" && (
                  <MenuGroupContent
                    searchPlaceholder={t("automation-form.search-fields")}
                    title={t("automation-form.fields-from-trigger")}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                    maxHeight={menuHeight}
                  >
                    {triggerOutputs
                      .filter((option) => !option.contextSpecific)
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            if (R.isTruthy(trigger.triggerReference)) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  {
                                    type: "triggerOutput",
                                    triggerReference: trigger.triggerReference,
                                    outputId: option.outputId,
                                  },
                                  isMultiple,
                                  editor,
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {option.title}
                          </Text>
                        </MenuOption>
                      ))}
                    {triggerOutputs.filter((option) => !option.contextSpecific)
                      .length > 1 &&
                      triggerOutputs.filter((option) => option.contextSpecific)
                        .length >= 1 && <MenuLineSeparator />}

                    {triggerOutputs
                      .filter((option) => option.contextSpecific)
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            if (R.isTruthy(trigger.triggerReference)) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  {
                                    type: "triggerOutput",
                                    triggerReference: trigger.triggerReference,
                                    outputId: option.outputId,
                                  },
                                  isMultiple,
                                  editor,
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {option.title}
                          </Text>
                        </MenuOption>
                      ))}
                  </MenuGroupContent>
                )}

                {tab === "members" && (
                  <MenuGroupContent
                    title={t("editor.members")}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                    maxHeight={menuHeight}
                  >
                    {members
                      .filter(
                        (member) =>
                          !pillList.some(
                            (pill) =>
                              pill.type === "member" &&
                              pill.value === member.memberId,
                          ),
                      )
                      .filter((member) =>
                        getMemberName(member)
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((member) => (
                        <AvatarMenuOption
                          key={member.memberId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            onChange(
                              mergeNextPillToInput(
                                config,
                                {
                                  type: "member",
                                  memberId: member.memberId,
                                },
                                isMultiple,
                                editor,
                              ),
                            );
                          }}
                          initials={nameToInitials(getMemberName(member))}
                          image={member.profileImageUrl}
                        >
                          {getMemberName(member)}
                        </AvatarMenuOption>
                      ))}
                  </MenuGroupContent>
                )}
                {tab === "teams" && (
                  <MenuGroupContent
                    title={t("editor.teams")}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                  >
                    {teams
                      .filter(
                        (team) =>
                          !pillList.some(
                            (pill) =>
                              pill.type === "team" &&
                              pill.value === team.teamId,
                          ),
                      )
                      .filter((team) =>
                        team.name
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((team) => (
                        <AvatarMenuOption
                          key={team.teamId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            onChange(
                              mergeNextPillToInput(
                                config,
                                {
                                  type: "team",
                                  teamId: team.teamId,
                                },
                                isMultiple,
                                editor,
                              ),
                            );
                          }}
                          initials={nameToInitials(team.name)}
                        >
                          {team.name}
                        </AvatarMenuOption>
                      ))}
                  </MenuGroupContent>
                )}
                {tab === "actions" && selectedAction !== undefined && (
                  <MenuGroupContent
                    title={t("field-popups.action-popup", {
                      name: selectedAction.name,
                    })}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                  >
                    {selectedAction.options
                      .filter((option) => !option.contextSpecific)
                      .filter(
                        (option) =>
                          !pillList.some(
                            (pill) =>
                              pill.type === "actionOutput" &&
                              pill.value === option.outputId,
                          ),
                      )
                      .filter((option) =>
                        option.title
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            if (
                              R.isTruthy(selectedAction.action.actionReference)
                            ) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  {
                                    type: "actionOutput",
                                    actionReference:
                                      selectedAction.action.actionReference,
                                    outputId: option.outputId,
                                  },
                                  isMultiple,
                                  editor,
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {option.title}
                          </Text>
                        </MenuOption>
                      ))}
                    {selectedAction.options.filter(
                      (option) => !option.contextSpecific,
                    ).length > 1 &&
                      selectedAction.options.filter(
                        (option) => option.contextSpecific,
                      ).length >= 1 && <MenuLineSeparator />}

                    {selectedAction.options
                      .filter((option) => option.contextSpecific)
                      .filter(
                        (option) =>
                          !pillList.some(
                            (pill) =>
                              pill.type === "actionOutput" &&
                              pill.value === option.outputId,
                          ),
                      )
                      .filter((option) =>
                        option.title
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            if (
                              R.isTruthy(selectedAction.action.actionReference)
                            ) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  {
                                    type: "actionOutput",
                                    actionReference:
                                      selectedAction.action.actionReference,
                                    outputId: option.outputId,
                                  },
                                  isMultiple,
                                  editor,
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {option.title}
                          </Text>
                        </MenuOption>
                      ))}
                  </MenuGroupContent>
                )}
              </DropdownPanel>
            )}
          </PickerInputField>
          {validationError !== "" && (
            <Paragraph color={"error"} size={"xs"}>
              {validationError}
            </Paragraph>
          )}
          <FieldMissingError message={getFieldMissingErrorMessage(config)} />
        </StyledReactInputFieldContainer>
      </Root>
    );
  },
);

const getMemberName = (member: Member): string =>
  member.name.length > 0 ? member.name : (member.phone[0] ?? "");
